// src/contexts/GuideContext.jsx
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useProductBrain } from './ProductBrainContext';
import { buildBrief } from '../lib/productInsights';
import { HABITS, MILESTONES, activationState, applyPatch, mergeActivation, visitPatch } from '../lib/activation';
import { detectEvents, selectTrigger, snapshotOf } from '../lib/activationTriggers';
import * as guide from '../services/activation';
import { collect, finale } from '../components/guide/motion';
import Spotlight from '../components/guide/Spotlight';
import QuickComposer from '../components/guide/QuickComposer';
import ConfirmClearSample from '../components/guide/ConfirmClearSample';
import '../components/guide/guide.css';

const Ctx = createContext(null);
export const useGuide = () => useContext(Ctx);
const SPOTLIGHT_KEY = 'spcc:guide:spotlight';
const readSession = key => { try { return sessionStorage.getItem(key); } catch { return null; } };
const writeSession = (key, value) => { try { sessionStorage.setItem(key, value); } catch { /* storage unavailable */ } };
const ALL = [...MILESTONES, ...HABITS];

// The guide waits while the user is in a dialog or typing.
export function isBusy(doc = document) {
  if (doc.querySelector('[role="dialog"]:not([data-guide-ui])')) return true;
  const el = doc.activeElement;
  return Boolean(el && (/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || el.isContentEditable) && !el.closest('[data-guide-ui]'));
}

export function GuideProvider({ children }) {
  const { data, loading, errors, workspace } = useProductBrain();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const saved = workspace?.onboarding?.activation;
  const entries = useRef([]);
  const [version, setVersion] = useState(0);
  // Unconfirmed patches are layered over the stored state. Patches are idempotent, so re-applying is safe.
  const activation = useMemo(() => workspace ? entries.current.reduce((a, e) => applyPatch(a, e.patch), mergeActivation(saved)) : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workspace, saved, version]);
  useEffect(() => { entries.current = entries.current.filter(e => !e.confirmed); }, [saved]);

  const update = useCallback(patch => {
    const retry = entries.current.filter(e => e.failed);
    const entry = { patch, confirmed: false, failed: false };
    entries.current = [...entries.current, entry];
    setVersion(v => v + 1);
    return Promise.all([...retry, entry].map(e => guide.saveActivation(e.patch).then(() => { e.confirmed = true; e.failed = false; }, error => { e.failed = true; console.warn('Guide progress was not saved yet.', error); }))).then(() => undefined);
  }, []);

  const hasErrors = Object.keys(errors || {}).length > 0;
  const ready = Boolean(activation?.started_at) && !loading && !hasErrors;
  const state = useMemo(() => activation ? activationState(data, activation) : null, [data, activation]);
  const briefIds = useMemo(() => buildBrief(data).map(item => item.id), [data]);
  const briefKey = briefIds.join('|');

  // Existing users skip /welcome; milestones they already reached are not celebrated again.
  useEffect(() => {
    if (!activation || loading || hasErrors || activation.started_at || !workspace?.onboarding?.status) return;
    update({ started_at: new Date().toISOString(), legacy: true, celebrated: activationState(data, activation).doneIds });
  }, [activation, loading, hasErrors, workspace, data, update]);

  useEffect(() => {
    if (!ready) return undefined;
    const timer = setTimeout(() => { const patch = visitPatch(activation, new Date(), briefIds); if (patch) update(patch); }, 2000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, briefKey, activation?.last_visit?.date]);

  const [events, setEvents] = useState([]);
  const previous = useRef(null);
  useEffect(() => {
    if (!ready) return;
    const snapshot = snapshotOf(data, activation, briefIds);
    const fresh = detectEvents(previous.current, snapshot);
    previous.current = snapshot;
    if (fresh.length) setEvents(list => [...list, ...fresh].slice(-20));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, data, activation?.sample_ids, briefKey]);

  const [busy, setBusy] = useState(false);
  useEffect(() => { const timer = setInterval(() => setBusy(isBusy()), 400); return () => clearInterval(timer); }, []);

  const lastTarget = useRef(null);
  useEffect(() => {
    const remember = e => { lastTarget.current = e.target; };
    document.addEventListener('pointerdown', remember, true);
    return () => document.removeEventListener('pointerdown', remember, true);
  }, []);

  const nodes = useRef({});
  const dockRef = useRef(null);
  const registerNode = useCallback((id, el) => { nodes.current[id] = el; }, []);
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    if (!ready || busy || pathname === '/welcome') return;
    const due = events.filter(e => e.startsWith('done:')).map(e => e.slice(5)).filter(id => !activation.celebrated.includes(id));
    const finaleDue = events.includes('activated') && !activation.celebrated.includes('activated');
    if (!due.length && !finaleDue) return;
    update(a => ({ celebrated: [...new Set([...a.celebrated, ...due, ...(finaleDue ? ['activated'] : [])])] }));
    // Habits have no ring node, so they fly to the ring itself.
    due.forEach(id => collect(lastTarget.current, nodes.current[id] || dockRef.current));
    const labels = due.map(id => ALL.find(m => m.id === id)?.label).filter(Boolean);
    if (labels.length) setAnnouncement(`Milestone complete: ${labels.join(', ')}.`);
    if (finaleDue) { setAnnouncement('Your product loop is live.'); finale(dockRef.current); }
  }, [events, busy, ready, pathname, activation, update]);

  const hidden = !ready || pathname === '/welcome' || Boolean(activation?.paused);
  const spotlightUsed = readSession(SPOTLIGHT_KEY) === '1';
  const trigger = useMemo(() => hidden ? null : selectTrigger(data, activation, { now: new Date(), busy, events, briefIds, spotlightUsed }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hidden, data, activation, busy, events, briefKey, spotlightUsed]);

  const [composer, setComposer] = useState(null);
  const [spotlight, setSpotlight] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const openSpotlight = useCallback(({ route, anchor, title = '', body = '' }) => {
    if (route && route !== pathname) navigate(route);
    setSpotlight({ anchor, title, body, route: route || pathname });
  }, [navigate, pathname]);
  // Leaving the spotlight's page closes it.
  useEffect(() => { if (spotlight && spotlight.route !== pathname) setSpotlight(null); }, [spotlight, pathname]);
  const showMilestone = useCallback(id => {
    const m = ALL.find(x => x.id === id);
    if (m?.show?.anchor) openSpotlight({ ...m.show, title: m.label, body: m.hint });
    else if (m?.show) navigate(m.show.route);
  }, [navigate, openSpotlight]);

  const runAction = useCallback(async action => {
    switch (action.type) {
      case 'composer': setComposer({ text: action.text || '' }); break;
      case 'navigate': navigate(action.to); break;
      case 'spotlight': openSpotlight(action); break;
      case 'show': showMilestone(action.milestoneId); break;
      case 'link-goal': await guide.linkGoal(action.initiativeId, action.objectiveId); break;
      case 'reminder': await guide.createGuideReminder({ initiativeId: action.initiativeId, dueAt: guide.tomorrowAt(new Date(), 9), title: 'Your morning brief is ready', description: 'See what changed in SPCC since yesterday.' }); break;
      case 'set-review': {
        const { review_date } = await guide.setReviewDate(action.decisionId, new Date());
        if (action.initiativeId) await guide.createGuideReminder({ initiativeId: action.initiativeId, dueAt: new Date(`${review_date}T09:00:00`), title: 'Decision review due', description: 'Compare what you expected with what happened.' });
        break;
      }
      case 'clear-sample': setConfirmClear(true); break;
      default: break;
    }
  }, [navigate, openSpotlight, showMilestone]);

  const resolve = useCallback((result, status) => {
    update({ triggers: { [result.instance.key]: { status, at: new Date().toISOString() } } });
    if (result.instance.events) setEvents(list => list.filter(e => !result.instance.events.includes(e)));
  }, [update]);
  // Errors propagate to the card, which keeps itself open and shows the message.
  const runPrimary = useCallback(async result => {
    if (result.trigger.surface === 'spotlight') writeSession(SPOTLIGHT_KEY, '1');
    await runAction(result.copy.primary.action);
    resolve(result, 'done');
  }, [runAction, resolve]);
  const runSecondary = useCallback(async result => { await runAction(result.copy.secondary.action); resolve(result, result.copy.secondary.action.type === 'dismiss' ? 'dismissed' : 'done'); }, [runAction, resolve]);
  const dismiss = useCallback(result => resolve(result, 'dismissed'), [resolve]);

  // A spotlight trigger runs itself when the user is already on its page.
  useEffect(() => {
    const action = trigger?.copy.primary.action;
    if (trigger?.trigger.surface === 'spotlight' && action?.route === pathname) runPrimary(trigger);
  }, [trigger, pathname, runPrimary]);

  const act = useCallback(key => update(a => ({ acted: a.acted.some(x => x.insight_key === key) ? a.acted : [{ insight_key: key, at: new Date().toISOString() }, ...a.acted].slice(0, 50) })), [update]);
  const sampleIds = activation?.sample_ids;
  const isSample = useCallback(id => Boolean(sampleIds?.includes(String(id))), [sampleIds]);
  const claim = useCallback(id => update(a => ({ sample_ids: a.sample_ids.filter(x => x !== String(id)) })), [update]);
  // Seeding records the sample IDs itself; the empty update refreshes the optimistic layer.
  const seed = useCallback(async () => { const result = await guide.seedSample(new Date()); await update({ path: 'sample' }); return result; }, [update]);
  const clear = useCallback(async () => { const result = await guide.clearSample(); await update({}); return result; }, [update]);
  const setPaused = useCallback(paused => update({ paused }), [update]);

  const value = {
    activation, state, ready, hidden, trigger, runPrimary, runSecondary, dismiss, update, act,
    registerNode, dockRef, composer, openComposer: (text = '') => setComposer({ text }), closeComposer: () => setComposer(null),
    spotlight, openSpotlight, closeSpotlight: () => setSpotlight(null), showMilestone,
    isSample, claim, seed, clear, confirmClear, setConfirmClear, setPaused, announcement,
  };
  return <Ctx.Provider value={value}>
    {children}
    {spotlight && <Spotlight/>}
    {composer && <QuickComposer/>}
    {confirmClear && <ConfirmClearSample/>}
    <div className="sr-only" role="status" aria-live="polite">{announcement}</div>
  </Ctx.Provider>;
}
